import { createSelector } from '@reduxjs/toolkit';
import { find, get, isEmpty, omit, reduce } from 'lodash';
import { getPropertyId, getRoomTypesWithoutOffers } from 'store/property/propertySelectors';
import { ExclusiveOfferState } from './exclusiveOfferReducer';
import { Offer } from 'types/property';

type PropertyRoomTypes = { id: string; maxOccupantCount?: number };
type PropertyRoomTypesWithOffers = PropertyRoomTypes & { offers: { id: string }[] };
type StoreState = {
  exclusiveOffer: ExclusiveOfferState;
};

export const getIsLoading = (state: StoreState) => get(state, 'exclusiveOffer.isLoading');
export const getExclusiveOffer = (state: StoreState) => get(state, 'exclusiveOffer.exclusiveOffer');
export const getExclusiveOfferAvailability = (state: StoreState) =>
  get(state, 'propertyAvailability.roomTypes', [] as PropertyRoomTypesWithOffers[]);
export const getExclusiveOfferAvailabilityQuery = (state: StoreState) =>
  get(state, 'propertyAvailability.meta.query', {} as Record<string, string>);
export const getLeadInOffer = createSelector(getExclusiveOffer, (exclusiveOffer) => exclusiveOffer?.rooms?.[0]?.offers?.[0]);
export const getPropertyInclusionSets = createSelector(getExclusiveOffer, (exclusiveOffer) => exclusiveOffer?.inclusionSets);
export const getExclusiveOfferRooms = createSelector(getExclusiveOffer, (exclusiveOffer) => exclusiveOffer?.rooms);
export const getExclusiveOfferRoomTypeCount = createSelector(getExclusiveOfferRooms, (rooms) => rooms?.length || 0);

export const getExclusiveOffersList = (state) => get(state, 'exclusiveOffer.exclusiveOffersList');
export const getIsExclusiveOfferAvailable = createSelector(
  getPropertyId,
  getExclusiveOffersList,
  (propertyId, exclusiveOffersList = []) => {
    if (!isEmpty(exclusiveOffersList)) {
      return exclusiveOffersList.some(({ propertyId: offerPropertyId }) => offerPropertyId === propertyId);
    }
    return false;
  },
);

export const getAllAvailableOffers = createSelector(getExclusiveOfferAvailability, (roomTypesList) => {
  const allOffers = roomTypesList?.reduce<Record<string, Offer>>((roomAccum, { offers }) => {
    const offersForThisRoom = offers.reduce((offerAccum, offer) => ({ ...offerAccum, [offer.id]: offer }), {});
    return { ...roomAccum, ...offersForThisRoom };
  }, {});
  return allOffers;
});

export const getExclusiveOfferAvailabilityCount = createSelector(
  getExclusiveOfferRooms,
  getAllAvailableOffers,
  (exclusiveRooms = [], availableOffers = {}) => {
    const availableOfferIds = Object.keys(availableOffers);
    return exclusiveRooms.reduce((counter, room) => {
      const exists = room.offers.some((offer) => availableOfferIds.includes(offer.id));
      return counter + (exists ? 1 : 0);
    }, 0);
  },
);

export const getExclusiveOfferInclusions = createSelector(getExclusiveOffer, (offer) => {
  const inclusions = reduce(
    offer?.highlights?.inclusions,
    (accum, inclusion) => {
      return [...accum, inclusion.name];
    },
    [],
  );
  return inclusions;
});

export const getExclusiveOfferRoomTypes = createSelector(
  getExclusiveOfferRooms,
  getRoomTypesWithoutOffers as (state: StoreState) => PropertyRoomTypes[],
  (exclusiveRoomTypes = [], roomTypesWithoutOffers = []) =>
    reduce(
      exclusiveRoomTypes,
      (cards, roomType) => {
        if (roomType?.offers.length === 0) return cards;

        const roomDetails = find(roomTypesWithoutOffers, ['id', roomType?.extranet?.id]);

        const {
          id: offerId,
          inclusions = [],
          adults = 0,
          children = 0,
          minNumberOfNights,
          offerTotal,
          valuedAtTotal = null,
          title,
          description,
        } = roomType.offers[0];

        const card = {
          ...omit(roomDetails, 'description'),
          extranet: roomType.extranet,
          exclusiveOffers: roomType.offers,
          offerId,
          inclusions,
          title,
          description,
          adults,
          children,
          maxOccupantCount: roomDetails?.maxOccupantCount || adults + children,
          minNumberOfNights,
          offerTotal,
          valuedAtTotal,
        };

        return [...cards, card];
      },
      [],
    ),
);

export const getExclusiveOfferSanityGa4Pricing = createSelector(getExclusiveOfferRooms, (rooms) => {
  const roomOffers = reduce(
    rooms,
    (accum, room) => {
      const offerDetails = reduce(
        room.offers,
        (accum, offer) => {
          const offerPricing = {
            offerTotal: offer?.offerTotal?.amount,
            valuedAtTotal: offer?.valuedAtTotal?.amount || 0,
            nights: offer?.minNumberOfNights,
            adults: offer?.adults,
            children: offer?.children,
          };
          return [...accum, offerPricing];
        },
        [],
      );
      return [...accum, ...offerDetails];
    },
    [],
  );

  const roomCount = rooms?.length;
  const offerCount = roomOffers?.length;

  const averagePrice = Math.floor(
    reduce(
      roomOffers,
      (accum, offer) => {
        const totalPayable = Number(offer?.offerTotal) || 0;
        return accum + totalPayable;
      },
      0,
    ) / offerCount,
  );

  const averagePriceBeforeDiscount = Math.floor(
    reduce(
      roomOffers,
      (accum, offer) => {
        const totalBeforeDiscount = Number(offer?.valuedAtTotal) || 0;
        return accum + totalBeforeDiscount;
      },
      0,
    ) / offerCount,
  );
  return {
    averagePrice: averagePrice,
    averagePriceBeforeDiscount: averagePriceBeforeDiscount,
    roomCount: roomCount,
    offerCount: offerCount,
  };
});

export const getExclusiveOffersAvailableGa4Pricing = createSelector(
  getAllAvailableOffers,
  getExclusiveOfferRoomTypes,
  (availableOffers, exclusiveOfferRoomTypes) => {
    const propertyOfferIds = reduce(
      exclusiveOfferRoomTypes,
      (accum, roomType) => {
        const roomOfferIds = reduce(
          roomType.exclusiveOffers,
          (accum, offer) => {
            return [...accum, offer.id];
          },
          [],
        );
        return [...accum, ...roomOfferIds];
      },
      [],
    );

    const availableExclusiveOffers = reduce(
      propertyOfferIds,
      (accum, offerId) => {
        const { [offerId]: offer } = availableOffers;
        if (offer === undefined) {
          return accum;
        } else {
          return [...accum, offer];
        }
      },
      [],
    );

    const roomCount = exclusiveOfferRoomTypes?.length;
    const offerCount = availableExclusiveOffers?.length;

    const averagePrice = Math.floor(
      reduce(
        availableExclusiveOffers,
        (accum, offer) => {
          const totalPayable = Number(offer?.charges?.total?.amount) || 0;
          return accum + totalPayable;
        },
        0,
      ) / offerCount,
    );

    const averagePriceBeforeDiscount = Math.floor(
      reduce(
        availableExclusiveOffers,
        (accum, offer) => {
          const totalBeforeDiscount = Number(offer?.charges?.total?.amount) + Number(offer?.charges?.totalDiscount?.amount) || 0;
          return accum + totalBeforeDiscount;
        },
        0,
      ) / offerCount,
    );

    const averagePointsEarned = Math.floor(
      reduce(
        availableExclusiveOffers,
        (accum, offer) => {
          const pointsEarned = offer?.pointsEarned?.qffPoints?.total || 0;
          return accum + pointsEarned;
        },
        0,
      ) / offerCount,
    );

    return {
      averagePrice: averagePrice,
      averagePriceBeforeDiscount: averagePriceBeforeDiscount,
      averagePointsEarned: averagePointsEarned,
      roomCount: roomCount,
      offerCount: offerCount,
    };
  },
);
